import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import { useSearchParams } from 'react-router-dom';

import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import SearchForm from '../../partials/actions/SearchForm';
import EngagementsTable from '../../partials/engagements/EngagementsTable';
import PaginationClassic from '../../components/PaginationClassic';
import CreateGapAnalysisModal from '../../components/grc/CreateGapAnalysisModal';

function GapAnalysis() {
  const [searchParams] = useSearchParams();
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [noResults, setNoResults] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [engagementModalOpen, setEngagementModalOpen] = useState(false);

  const [frameworks, setFrameworks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [userRole, setUserRole] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState([]);

  useEffect(() => {
    if (searchParams.get('openModal') === 'true') {
      setEngagementModalOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchFrameworks = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${Config.APIBaseURL}/frameworks/`, {
          headers: { access_token: `Bearer ${token}` },
          withCredentials: true,
        });
        // Real ids. They were shifted by one so that id could double as an
        // array index, which holds only while ids are contiguous from 1.
        setFrameworks(response.data.map((fw) => ({ id: fw.id, label: fw.name })));
      } catch (error) {
        console.error('Error fetching frameworks:', error);
      }
    };
    fetchFrameworks();
  }, []);

  useEffect(() => {
    try {
      const role = JSON.parse(localStorage.getItem('role'));
      setUserRole(role);
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  }, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${Config.APIBaseURL}/customers/`, {
          headers: { access_token: `Bearer ${token}` },
          withCredentials: true,
        });
        if (Array.isArray(response.data)) {
          setCustomers(response.data.map((c) => ({ id: c.id, label: c.short_name })));
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        if (userRole === 'user') {
          try {
            const userInfo = JSON.parse(localStorage.getItem('userinfo'));
            if (userInfo?.org && userInfo?.org_name) {
              setCustomers([{ id: userInfo.org, label: userInfo.org_name }]);
            }
          } catch (e) { /* ignore */ }
        }
      }
    };
    fetchCustomers();
  }, [userRole]);

  const loadEngagements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${Config.APIBaseURL}/engagements/?subtype_id=7`, {
        headers: { access_token: `Bearer ${token}` },
        withCredentials: true,
      });
      if (res.data) setEngagements(res.data);
    } catch (error) {
      console.error('Error loading engagements:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadEngagements();
  }, []);

  const searchEngagements = async (term) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${Config.APIBaseURL}/search_eng/${term}`, {
        headers: { access_token: `Bearer ${token}` },
        withCredentials: true,
      });
      if (res?.data?.length > 0) {
        const filtered = res.data.filter((eng) => eng.subtype_id === 7);
        if (filtered.length > 0) {
          setNoResults(false);
          setEngagements(filtered);
        } else {
          setNoResults(true);
          setEngagements([]);
        }
      } else {
        setNoResults(true);
        setEngagements([]);
      }
    } catch (error) {
      console.error('Error searching:', error);
      setNoResults(true);
      setEngagements([]);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    if (searchTerm.trim() !== '') searchEngagements(searchTerm);
  };

  const handleSelectedItems = (items) => setSelectedItems([...items]);

  const deleteEngagement = async (ids) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${Config.APIBaseURL}/engagement/delete`,
        { engagements: ids },
        {
          headers: { access_token: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      if (res.status === 200) {
        setEngagements((prev) => prev.filter((eng) => !ids.includes(eng.id)));
        setShowConfirmModal(false);
      }
    } catch (error) {
      console.error('Error deleting engagements:', error);
    }
  };

  const handleDeleteClick = (ids) => {
    setIdsToDelete(ids);
    setShowConfirmModal(true);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            {/* Page header */}
            <div className="sm:flex sm:justify-between sm:items-center mb-5">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl text-slate-800 dark:text-slate-100 font-bold">Gap Analysis</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Compliance assessment engagements against regulatory frameworks.
                </p>
              </div>
              <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                <SearchForm placeholder="Search" />
                <button
                  className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
                  onClick={(e) => { e.stopPropagation(); setEngagementModalOpen(true); }}
                >
                  <svg className="w-4 h-4 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                    <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                  </svg>
                  <span className="xs:block ml-2">New Assessment</span>
                </button>
              </div>
            </div>

            {/* Status filters */}
            <div className="sm:flex sm:justify-between sm:items-center mb-5">
              <div className="mb-4 sm:mb-0">
                <ul className="flex flex-wrap -m-1">
                  <li className="m-1">
                    <button className="inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-transparent shadow-sm bg-indigo-500 text-white">
                      All <span className="ml-1 text-indigo-200">{engagements.length}</span>
                    </button>
                  </li>
                  {['Not Started', 'In Progress', 'Finished', 'Submitted'].map((label) => {
                    const statusKey = label.toLowerCase();
                    const count = engagements.filter((e) => e.status === statusKey).length;
                    return (
                      <li key={label} className="m-1">
                        <button className="inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                          {label} <span className="ml-1 text-slate-400 dark:text-slate-500">{count}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Table */}
            <EngagementsTable
              title="Gap Analysis"
              engagements={engagements}
              setEngagements={setEngagements}
              selectedItems={handleSelectedItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onDeleteEngagement={deleteEngagement}
              showConfirmModal={showConfirmModal}
              setShowConfirmModal={setShowConfirmModal}
              handleDeleteClick={handleDeleteClick}
              idsToDelete={idsToDelete}
              onCreateNew={() => setEngagementModalOpen(true)}
              userRole={userRole}
            />

            <div className="mt-8">
              <PaginationClassic
                currentPage={currentPage}
                totalItems={engagements ? engagements.length : 0}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Create Modal */}
      <CreateGapAnalysisModal
        open={engagementModalOpen}
        onClose={() => setEngagementModalOpen(false)}
        onCreated={(newEng) => {
          setEngagements((prev) => [...prev, newEng]);
        }}
        userRole={userRole}
        customers={customers}
        frameworks={frameworks}
      />
    </div>
  );
}

export default GapAnalysis;
